export interface PendingBranch {
    name?: string;
    pincode?: string;
    landmark?: string;
    description?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteBranch {
    awaitingName?: boolean;
}

export const startCreateBranchFlow = (
    setPendingBranch: (data: PendingBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingBranch({ name: nameFromIntent });

        pushAIChat(`Great! What is the pincode for "${nameFromIntent}" branch?`);
        speak(`What is the pincode for ${nameFromIntent} branch?`);
        return;
    }

    setPendingBranch({});
    pushAIChat("Sure! What should the branch be called?");
    speak("What should the branch be called?");
};

export const processPendingBranch = (
    text: string,
    pending: PendingBranch,
    setPendingBranch: (data: PendingBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Branch Name
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid branch name.");
            speak("Please provide a valid branch name.");
            return;
        }

        setPendingBranch({ ...pending, name: cleanName });

        pushAIChat(`What is the pincode for "${cleanName}" branch?`);
        speak(`What is the pincode for ${cleanName} branch?`);
        return;
    }

    // STEP 2 — Pincode
    if (!pending.pincode) {
        const pincode = text.replace(/\D/g, "");

        if (!/^\d{6}$/.test(pincode)) {
            pushAIChat("Please provide a valid 6-digit pincode.");
            speak("Please provide a valid 6 digit pincode.");
            return;
        }

        setPendingBranch({ ...pending, pincode });

        pushAIChat("Any landmark for this branch?");
        speak("Any landmark for this branch?");
        return;
    }

    // STEP 3 — Landmark
    if (!pending.landmark) {
        setPendingBranch({ ...pending, landmark: text });

        pushAIChat("Any description for this branch?");
        speak("Any description?");
        return;
    }

    // STEP 4 — Description
    if (!pending.description) {
        setPendingBranch({ ...pending, description: text });

        pushAIChat("Should this branch be active or inactive?");
        speak("Should this branch be active or inactive?");
        return;
    }

    // STEP 5 — Status
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        // FINAL — CREATE BRANCH
        pushAIChat(`Perfect! Creating branch "${pending.name}"...`);
        speak(`Creating branch ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-branch", {
                detail: {
                    name: pending.name,
                    pincode: pending.pincode,
                    landmark: pending.landmark,
                    description: pending.description,
                    status
                }
            })
        );

        setPendingBranch(null);
        return;
    }
};

export const startDeleteBranchFlow = (
    setPendingDelete: (data: PendingDeleteBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting branch "${nameFromIntent}"...`);
        speak(`Deleting branch ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-branch", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which branch would you like to delete?");
    speak("Which branch would you like to delete?");
};

export const processPendingDeleteBranch = (
    text: string,
    pending: PendingDeleteBranch,
    setPendingDelete: (data: PendingDeleteBranch | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid branch name.");
        speak("Please provide a valid branch name.");
        return;
    }

    pushAIChat(`Deleting branch "${cleanName}"...`);
    speak(`Deleting branch ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-branch", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};